/**
 * SiteContext — bootstrap parallelo via headless API.
 * Carica settings + nav-header + nav-footer in una sola Promise.all.
 * Tenant e space sono risolti dall'hostname (vedi lib/site.ts).
 */
import { createContext, useContext, useEffect, useState } from 'react';
import {
  fetchHeadlessSettings,
  applyBranding,
  BRANDING_DEFAULTS,
  type SiteBranding,
} from '../lib/branding';
import { headlessGet } from '../lib/api';
import type { CmsNavItem, CmsLayoutConfig } from '../types/cms';
import { DEFAULT_LAYOUT } from '../types/cms';

interface SiteState {
  branding: SiteBranding;
  headerItems: CmsNavItem[];
  footerItems: CmsNavItem[];
  homepageSlug: string | null;
  layoutConfig: CmsLayoutConfig;
  isPrivateSite: boolean;
  isBootstrapping: boolean;
  trackingWriteKey: string | null;
}

const SiteContext = createContext<SiteState>({
  branding: BRANDING_DEFAULTS,
  headerItems: [],
  footerItems: [],
  homepageSlug: null,
  layoutConfig: DEFAULT_LAYOUT,
  isPrivateSite: false,
  isBootstrapping: true,
  trackingWriteKey: null,
});

async function fetchNav(
  menuSlug: string,
): Promise<{ homepageSlug: string | null; items: CmsNavItem[] }> {
  try {
    return await headlessGet<{ homepageSlug: string | null; items: CmsNavItem[] }>(
      `/nav?menu=${encodeURIComponent(menuSlug)}`,
    );
  } catch {
    return { homepageSlug: null, items: [] };
  }
}

export function SiteProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SiteState>({
    branding: BRANDING_DEFAULTS,
    headerItems: [],
    footerItems: [],
    homepageSlug: null,
    layoutConfig: DEFAULT_LAYOUT,
    isPrivateSite: false,
    isBootstrapping: true,
    trackingWriteKey: null,
  });

  useEffect(() => {
    let mounted = true;

    (async () => {
      // Parallel fetch: settings + both navs in one shot
      const [settings, headerNav, footerNav] = await Promise.all([
        fetchHeadlessSettings(),
        fetchNav('header'),
        fetchNav('footer'),
      ]);

      if (!mounted) return;

      applyBranding(settings);

      // Redirect to custom domain if configured and we're on the default subdomain
      if (
        settings.customDomain &&
        settings.redirectMode === 'to_custom' &&
        !window.location.hostname.includes(settings.customDomain)
      ) {
        window.location.replace(
          `https://${settings.customDomain}${window.location.pathname}${window.location.search}`,
        );
        return;
      }

      const layoutConfig: CmsLayoutConfig = {
        contentWidth:
          (settings.layout.contentWidth as CmsLayoutConfig['contentWidth']) ??
          DEFAULT_LAYOUT.contentWidth,
        headerStyle:
          (settings.layout.headerStyle as CmsLayoutConfig['headerStyle']) ??
          DEFAULT_LAYOUT.headerStyle,
        navPosition:
          (settings.layout.navPosition as CmsLayoutConfig['navPosition']) ??
          DEFAULT_LAYOUT.navPosition,
        footerStyle:
          (settings.layout.footerStyle as CmsLayoutConfig['footerStyle']) ??
          DEFAULT_LAYOUT.footerStyle,
      };

      setState({
        branding: {
          name: settings.name,
          logoUrl: settings.logoUrl,
          primaryColor: settings.primaryColor,
          secondaryColor: settings.secondaryColor,
          fontFamily: settings.fontFamily,
          faviconUrl: settings.faviconUrl,
        },
        headerItems: headerNav.items,
        footerItems: footerNav.items,
        homepageSlug:
          settings.homepageSlug ?? headerNav.homepageSlug ?? footerNav.homepageSlug,
        layoutConfig,
        isPrivateSite: settings.isPrivate,
        isBootstrapping: false,
        trackingWriteKey: settings.trackingWriteKey,
      });
    })();

    return () => {
      mounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <SiteContext.Provider value={state}>{children}</SiteContext.Provider>;
}

export function useSite(): SiteState {
  return useContext(SiteContext);
}

export function useBranding(): SiteBranding {
  return useContext(SiteContext).branding;
}

export function useHeaderNav(): CmsNavItem[] {
  return useContext(SiteContext).headerItems;
}

export function useFooterNav(): CmsNavItem[] {
  return useContext(SiteContext).footerItems;
}

export function useHomepageSlug(): string | null {
  return useContext(SiteContext).homepageSlug;
}

export function useSiteLayout(): CmsLayoutConfig {
  return useContext(SiteContext).layoutConfig;
}

export function useIsPrivateSite(): boolean {
  return useContext(SiteContext).isPrivateSite;
}

export function useIsBootstrapping(): boolean {
  return useContext(SiteContext).isBootstrapping;
}
